import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Download, Search, Wallet, X } from 'lucide-react';
import { adminSubscriptionsAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import PaymentReport from './PaymentReport';

const formatAdminDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}-${month}-${d.getFullYear()}`;
};

const formatAmount = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN')}`;

const STATUS_STYLES = {
  processed: 'bg-green-50 text-green-700',
  created: 'bg-amber-50 text-amber-700',
  failed: 'bg-red-50 text-red-700'
};

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'processed', label: 'Processed' },
  { value: 'created', label: 'Created' },
  { value: 'failed', label: 'Failed' }
];

const PAGE_LIMIT = 20;

export default function SettlementsTab() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [reportKey, setReportKey] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminSubscriptionsAPI.getSettlements({
        page,
        limit: PAGE_LIMIT,
        ...(status ? { status } : {}),
        ...(search ? { search } : {})
      });
      setData(res.data.data);
    } catch (err) {
      toast.error(err.error || 'Failed to load settlements');
    } finally {
      setLoading(false);
    }
  }, [page, status, search, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data: res } = await adminSubscriptionsAPI.syncSettlements();
      toast.success(res.data?.message || 'Settlements synced');
      if (page !== 1) setPage(1); else fetchData();
      setReportKey((k) => k + 1);
    } catch (err) {
      toast.error(err.error || 'Failed to sync settlements');
    } finally {
      setSyncing(false);
    }
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const overview = data?.overview;
  const settlements = data?.settlements || [];
  const pagination = data?.pagination;

  // awaitingSettlement is Razorpay's real "Available balance" (null until first sync)
  const awaiting = overview?.awaitingSettlement;

  // Global at-a-glance stats (independent of list filters)
  const overviewCards = [
    { label: 'Total Revenue', value: formatAmount(overview?.grossRevenue), tone: 'text-surface-900', hint: `${overview?.paymentCount || 0} payment${overview?.paymentCount === 1 ? '' : 's'}` },
    { label: 'Settled to Bank', value: formatAmount(overview?.settledAmount), tone: 'text-green-700', hint: `${overview?.settledCount || 0} done` },
    { label: 'Pending Settlement', value: awaiting == null ? 'Sync needed' : formatAmount(awaiting), tone: 'text-amber-700', hint: awaiting == null ? 'Tap Sync from Razorpay' : 'Available balance' },
    { label: 'Fees + Tax', value: formatAmount((overview?.totalFees || 0) + (overview?.totalTax || 0)), tone: 'text-surface-500', hint: `Refunds ${formatAmount(overview?.refundTotal)}` }
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-primary-600" />
          <h2 className="text-[15px] sm:text-xl font-semibold text-surface-900">Settlements</h2>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-medium text-blue-700 bg-blue-50 rounded-xl hover:bg-blue-100 disabled:opacity-50 transition-colors"
        >
          {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {syncing ? 'Syncing...' : 'Sync from Razorpay'}
        </button>
      </div>

      {/* Global overview cards (all-time, independent of list filters) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {overviewCards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl p-3 border border-surface-200">
            <span className="text-[10px] sm:text-xs font-medium text-surface-500 uppercase">{c.label}</span>
            <p className={`text-base sm:text-lg font-bold ${c.tone}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {c.value}
            </p>
            {c.hint && <span className="text-[10px] sm:text-[11px] text-surface-400">{c.hint}</span>}
          </div>
        ))}
      </div>
      {overview && (
        <p className="text-[11px] sm:text-xs text-surface-400 -mt-2">
          {overview.settledCount} settled · {overview.pendingCount} pending · {overview.failedCount} failed
          {awaiting != null && ` · Available balance ${formatAmount(awaiting)}`}
          {overview.balanceComputedAt && ` (as of ${formatAdminDate(overview.balanceComputedAt)})`}
        </p>
      )}

      {/* Filters + list */}
      <div className="bg-white rounded-2xl border border-surface-200 p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
          <form onSubmit={submitSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search UTR or settlement ID..."
              className="w-full pl-9 pr-8 py-2.5 bg-white border border-surface-200 rounded-xl text-sm text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {search && (
              <button type="button" onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </form>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="px-3 py-2.5 bg-white border border-surface-200 rounded-xl text-sm text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
          </div>
        ) : settlements.length === 0 ? (
          <div className="text-center py-12 text-surface-500">
            <Wallet className="w-12 h-12 mx-auto mb-3 text-surface-300" />
            <p>No settlements found</p>
            <p className="text-xs text-surface-400 mt-1">Click “Sync from Razorpay” to pull the latest.</p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="xl:hidden space-y-3">
              {settlements.map((s) => (
                <div key={s.razorpaySettlementId} className="border border-surface-200 rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold capitalize ${STATUS_STYLES[s.status] || 'bg-surface-100 text-surface-600'}`}>
                      {s.status}
                    </span>
                    <span className="text-sm font-bold text-surface-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(s.amount)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-surface-500">
                    <span className="font-mono">{s.utr || '—'}</span>
                    <span>{formatAdminDate(s.razorpayCreatedAt)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-surface-400">
                    <span>Fees {formatAmount(s.fees)} · Tax {formatAmount(s.tax)}</span>
                    <span className="font-mono">{s.razorpaySettlementId}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden xl:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-surface-400 border-b border-surface-100">
                    <th className="py-2 pr-3 font-semibold">Settlement ID</th>
                    <th className="py-2 px-3 font-semibold">Created</th>
                    <th className="py-2 px-3 font-semibold text-right">Amount</th>
                    <th className="py-2 px-3 font-semibold text-right">Settled</th>
                    <th className="py-2 px-3 font-semibold text-right">Fees</th>
                    <th className="py-2 px-3 font-semibold text-right">Tax</th>
                    <th className="py-2 px-3 font-semibold">UTR</th>
                    <th className="py-2 pl-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((s) => (
                    <tr key={s.razorpaySettlementId} className="border-b border-surface-50 last:border-0">
                      <td className="py-2.5 pr-3 font-mono text-[12px] text-surface-600">{s.razorpaySettlementId}</td>
                      <td className="py-2.5 px-3 text-surface-500">{formatAdminDate(s.razorpayCreatedAt)}</td>
                      <td className="py-2.5 px-3 text-right font-semibold text-surface-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(s.amount)}</td>
                      <td className="py-2.5 px-3 text-right text-surface-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(s.amountSettled)}</td>
                      <td className="py-2.5 px-3 text-right text-amber-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(s.fees)}</td>
                      <td className="py-2.5 px-3 text-right text-amber-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(s.tax)}</td>
                      <td className="py-2.5 px-3 font-mono text-[12px] text-surface-500">{s.utr || '—'}</td>
                      <td className="py-2.5 pl-3">
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold capitalize ${STATUS_STYLES[s.status] || 'bg-surface-100 text-surface-600'}`}>
                          {s.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-surface-100">
                <span className="text-xs text-surface-500">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} total
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-surface-200 text-surface-700 disabled:opacity-40 hover:bg-surface-50"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={page >= pagination.pages}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-surface-200 text-surface-700 disabled:opacity-40 hover:bg-surface-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Revenue breakdown (settlement blocks hidden — handled above) */}
      <div className="bg-white rounded-2xl border border-surface-200 p-3 sm:p-6">
        <h3 className="text-[15px] sm:text-lg font-semibold text-surface-900 mb-4">Revenue Report</h3>
        <PaymentReport key={reportKey} showSettlements={false} />
      </div>
    </div>
  );
}
